import { Cat, Dog, User, Sponsor } from '../types';
import { initializeApp, getApp, deleteApp } from 'firebase/app';
import { 
  getFirestore, collection, getDocs, doc, setDoc, deleteDoc, updateDoc, getDoc, writeBatch
} from 'firebase/firestore';
import { 
  getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signInWithRedirect, getRedirectResult 
} from 'firebase/auth';

// --- FIREBASE CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyDLyyKuxRWMNnif1VcFigsxnFXkXCFELws",
  authDomain: "tierheim-mallorca.firebaseapp.com",
  projectId: "tierheim-mallorca",
  storageBucket: "tierheim-mallorca.firebasestorage.app",
  messagingSenderId: "511274526744",
  appId: "1:511274526744:web:ae64ef9e706a4bdd813b6d",
  measurementId: "G-D4MY0VZVX1"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Export specific firebase functions used in components
export { signInWithPopup, signInWithEmailAndPassword, signInWithRedirect, getRedirectResult };

// Utility: remove undefined values so Firestore rejects are avoided
const stripUndefined = <T extends Record<string, any>>(obj: T): T => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      (acc as any)[key] = value;
    }
    return acc;
  }, {} as T);
};

// --- IMAGE COMPRESSION ---
export const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 800;
        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        resolve(dataUrl);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

// --- DATA FETCHING ---

export const getCats = async (): Promise<Cat[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'cats'));
    const allCats = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      let cleanedVacs: any[] = [];
      if (Array.isArray(data.vaccinations)) {
          cleanedVacs = data.vaccinations.map((v: any) => {
              if (typeof v === 'string') return { id: crypto.randomUUID(), name: v, date: '' };
              if (typeof v === 'object' && v !== null) {
                  return {
                      id: v.id || crypto.randomUUID(),
                      name: v.name || 'Unbekannt',
                      date: v.date || ''
                  };
              }
              return null;
          }).filter(v => v !== null); 
      }

      return { 
        ...data, 
        id: doc.id, // FORCE ID from Document
        vaccinations: cleanedVacs,
        photos: Array.isArray(data.photos) ? data.photos : [],
        terrain: data.terrain || 1 // Default to 1 if missing
      } as Cat;
    });

    // Filter out deleted items (compatibility with soft-delete attempts)
    // AND filter out broken records without names
    return allCats.filter(cat => !cat.deleted && cat.name);

  } catch (e) {
    console.error("Error fetching cats: ", e);
    return [];
  }
};

export const saveCat = async (cat: Cat): Promise<void> => {
  try {
    const catId = cat.id.trim();
    if (!catId) throw new Error("ID is empty");

    // Remove undefined optional fields to satisfy Firestore
    const cleanCat = stripUndefined({
      ...cat,
      chip: cat.chip ? stripUndefined(cat.chip) : undefined,
      nextVaccination: cat.nextVaccination ? stripUndefined(cat.nextVaccination) : undefined,
      notes: cat.notes || undefined,
      passportNumber: cat.passportNumber || undefined,
      deathDate: cat.deathDate || undefined,
      birthDate: cat.birthDate || undefined,
      godparent: cat.godparent || undefined,
    });

    await setDoc(doc(db, 'cats', catId), cleanCat);
  } catch (e) {
    console.error("Error adding document: ", e);
    alert("Fehler beim Speichern: " + e);
  }
};

// --- ROBUST DELETE FUNCTION ---
export const deleteCat = async (rawId: string): Promise<void> => {
  if (!rawId) throw new Error("ID is missing");
  const id = rawId.trim(); // Critical: Trim whitespace
  
  console.log(`[DB] Attempting HARD delete for: '${id}'`);
  
  try {
    const docRef = doc(db, 'cats', id);
    
    // 1. Try Hard Delete
    await deleteDoc(docRef);
    console.log(`[DB] Delete executed for ${id}`);

  } catch (error: any) {
    console.error(`[DB] DELETE ERROR for ${id}:`, error);
    alert(`Lösch-Fehler: ${error.message}`);
    throw error;
  }
};

// --- BATCH DELETE (NUCLEAR OPTION) ---
export const clearAllCats = async (): Promise<void> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'cats'));
    const total = querySnapshot.size;
    console.log(`[DB] Batch deleting ${total} cats...`);

    if (total === 0) return;

    // Firestore allows max 500 ops per batch.
    // We create a new batch for safety.
    const batch = writeBatch(db);
    
    querySnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log("[DB] Batch delete committed successfully.");
  } catch (e: any) {
    console.error("Error clearing database:", e);
    alert("Fehler beim Leeren der DB: " + e.message);
    throw e;
  }
};

// --- DOG DATA FUNCTIONS ---

export const getDogs = async (): Promise<Dog[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'dogs'));
    const allDogs = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      let cleanedVacs: any[] = [];
      if (Array.isArray(data.vaccinations)) {
          cleanedVacs = data.vaccinations.map((v: any) => {
              if (typeof v === 'string') return { id: crypto.randomUUID(), name: v, date: '' };
              if (typeof v === 'object' && v !== null) {
                  return {
                      id: v.id || crypto.randomUUID(),
                      name: v.name || 'Unbekannt',
                      date: v.date || ''
                  };
              }
              return null;
          }).filter(v => v !== null); 
      }

      return { 
        ...data, 
        id: doc.id,
        vaccinations: cleanedVacs,
        photos: Array.isArray(data.photos) ? data.photos : [],
        terrain: data.terrain || 1
      } as Dog;
    });

    return allDogs.filter(dog => !dog.deleted && dog.name);

  } catch (e) {
    console.error("Error fetching dogs: ", e);
    return [];
  }
};

export const saveDog = async (dog: Dog): Promise<void> => {
  try {
    const dogId = dog.id.trim();
    if (!dogId) throw new Error("ID is empty");

    const cleanDog = stripUndefined({
      ...dog,
      chip: dog.chip ? stripUndefined(dog.chip) : undefined,
      nextVaccination: dog.nextVaccination ? stripUndefined(dog.nextVaccination) : undefined,
      notes: dog.notes || undefined,
      passportNumber: dog.passportNumber || undefined,
      deathDate: dog.deathDate || undefined,
      birthDate: dog.birthDate || undefined,
      godparent: dog.godparent || undefined,
    });

    await setDoc(doc(db, 'dogs', dogId), cleanDog);
  } catch (e) {
    console.error("Error adding dog document: ", e);
    alert("Fehler beim Speichern: " + e);
  }
};

export const deleteDog = async (rawId: string): Promise<void> => {
  if (!rawId) throw new Error("ID is missing");
  const id = rawId.trim();
  
  console.log(`[DB] Attempting HARD delete for dog: '${id}'`);
  
  try {
    const docRef = doc(db, 'dogs', id);
    await deleteDoc(docRef);
    console.log(`[DB] Delete executed for ${id}`);

  } catch (error: any) {
    console.error(`[DB] DELETE ERROR for ${id}:`, error);
    alert(`Lösch-Fehler: ${error.message}`);
    throw error;
  }
};

export const clearAllDogs = async (): Promise<void> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'dogs'));
    const total = querySnapshot.size;
    console.log(`[DB] Batch deleting ${total} dogs...`);

    if (total === 0) return;

    const batch = writeBatch(db);
    
    querySnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log("[DB] Batch delete committed successfully.");
  } catch (e: any) {
    console.error("Error clearing database:", e);
    alert("Fehler beim Leeren der DB: " + e.message);
    throw e;
  }
};

export const getUniqueDogValues = async () => {
  const dogs = await getDogs();
  const sponsors = await getSponsors();
  const names = Array.from(new Set(dogs.map(d => d.name))).sort();
  const allVacs = dogs.flatMap(d => (d.vaccinations || []).map(v => v.name));
  const vaccinations = Array.from(new Set(allVacs.filter(Boolean))).sort();
  const godparents = sponsors.map(s => s.name).sort();
  return { names, vaccinations, godparents };
};

// --- USER MANAGEMENT ---

export const getUsers = async (): Promise<User[]> => {
  const querySnapshot = await getDocs(collection(db, 'users'));
  return querySnapshot.docs.map(doc => doc.data() as User);
};

export const saveUser = async (user: User): Promise<void> => {
  await setDoc(doc(db, 'users', user.email), user);
};

export const deleteUser = async (email: string): Promise<void> => {
  await deleteDoc(doc(db, 'users', email));
};

export const createSystemUser = async (email: string, password: string, name: string, role: 'admin' | 'user'): Promise<void> => {
  const tempAppName = 'secondaryUserCreator';
  let tempApp;
  try {
    tempApp = getApp(tempAppName);
  } catch (e) {
    tempApp = initializeApp(firebaseConfig, tempAppName);
  }

  const tempAuth = getAuth(tempApp);
  await createUserWithEmailAndPassword(tempAuth, email, password);

  const newUser: User = {
    email,
    name,
    role,
    isApproved: true
  };
  await saveUser(newUser);

  await signOut(tempAuth);
  deleteApp(tempApp).catch(console.error);
};

export const handleUserLogin = async (firebaseUser: any): Promise<{ user: User, error?: string }> => {
  const email = firebaseUser.email;
  const name = firebaseUser.displayName || email.split('@')[0];

  const users = await getUsers();
  const existingUser = users.find(u => u.email === email);

  if (existingUser) {
    if (!existingUser.isApproved) {
      return { user: existingUser, error: 'pending_approval' };
    }
    return { user: existingUser };
  } else {
    const isFirstUser = users.length === 0;
    const newUser: User = {
      email,
      name,
      role: isFirstUser ? 'admin' : 'user', 
      isApproved: isFirstUser ? true : false
    };
    await saveUser(newUser);
    return { user: newUser, error: isFirstUser ? undefined : 'pending_approval' };
  }
};

export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('currentUser');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }
  return null;
};

// --- SPONSOR MANAGEMENT ---

export const getSponsors = async (): Promise<Sponsor[]> => {
  const querySnapshot = await getDocs(collection(db, 'sponsors'));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Sponsor));
};

export const saveSponsor = async (sponsor: Sponsor): Promise<void> => {
  const { id, ...data } = sponsor;
  const cleanData = stripUndefined(data);
  await setDoc(doc(db, 'sponsors', id), cleanData);
};

export const deleteSponsor = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, 'sponsors', id));
};

export const getUniqueValues = async () => {
  const cats = await getCats();
  const sponsors = await getSponsors();
  const names = Array.from(new Set(cats.map(c => c.name))).sort();
  const allVacs = cats.flatMap(c => (c.vaccinations || []).map(v => v.name));
  const vaccinations = Array.from(new Set(allVacs.filter(Boolean))).sort();
  const godparents = sponsors.map(s => s.name).sort();
  return { names, vaccinations, godparents };
};

// --- DATABASE EXPORT ---
export const exportDatabase = async (): Promise<void> => {
  try {
    const [cats, users, sponsors] = await Promise.all([
      getCats(),
      getUsers(),
      getSponsors()
    ]);

    const data = {
      cats,
      users,
      sponsors,
      exportedAt: new Date().toISOString()
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `purrfectmanager_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting database:', error);
    alert('Error al exportar la base de datos: ' + error);
  }
};